import {
  Box,
  Button,
  Container,
  Field,
  Flex,
  Heading,
  Input,
  Text,
  Avatar,
  FileUpload,
  Icon,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useColorModeValue } from "../UI/color-mode"

import { type ApiError, MediaService, type UserUpdateMe, UsersService } from "../../client"
import useAuth from "../../hooks/useAuth"
import useCustomToast from "../../hooks/useCustomToast"
import { emailPattern, handleError, loadImageFile, userHasPermission } from "../../utils"
import FileDropzone from "../Common/FileDropzone"
import { FiUpload } from "react-icons/fi"
import { waitForCelerySuccess } from "../../utils"

// Permission check for media create and read
const [me_create, me_read] = ["create", "read"].map(
  (action) => userHasPermission([{ perm: `media_${action}`, is_allowed: false }])[0].is_allowed
)


const UserInformation = () => {
  const queryClient = useQueryClient()
  const showToast = useCustomToast()

  const color = useColorModeValue("inherit", "light")

  const [editMode, setEditMode] = useState(false)

  const { user: currentUser } = useAuth()

  const defaultVals = {
    firstname: currentUser?.firstname,
    lastname: currentUser?.lastname,
    email: currentUser?.email,
    fk_media_profile_pic_id: currentUser?.fk_media_profile_pic?.id,
  }

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting, errors, isDirty },
  } = useForm<UserUpdateMe>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: defaultVals,
  })

  const toggleEditMode = () => {
    setEditMode(!editMode)
  }

  // Profile pic
  const [file, setFile] = useState<File | null>(null)
  const [prevProfileFile, setPrevProfileFile] = useState<File | null>(null)

  const previousFile = async () => {
    if(currentUser?.fk_media_profile_pic) {
      const file = await loadImageFile(currentUser.fk_media_profile_pic.path, currentUser.fk_media_profile_pic.file_name)

      if(file) setPrevProfileFile(file)
    }
  }

  useEffect(() => {
    if (!currentUser || !editMode ) return

    previousFile()

    reset(defaultVals)
  }, [currentUser, editMode])

  const mutation = useMutation({
    mutationFn: (data: UserUpdateMe) =>
      UsersService.updateUserMe({ requestBody: data }),
    onSuccess: () => {
      showToast("Aktion erfolgreich!", "Benutzer wurde angepasst", "success")
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ["currentUser"]})
    },
  })

  const mutationMedia = useMutation({
    mutationFn: (data: File) =>
      MediaService.createMedia({ formData: { file: data } }),
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] })
    },
  })

  const onSubmit: SubmitHandler<UserUpdateMe> = async (data) => {
    if(file) {
      const media = await mutationMedia.mutateAsync(file)
      data.fk_media_profile_pic_id = media.id

      if (media.task_id) await waitForCelerySuccess(media.task_id)
    } else if(!file && !prevProfileFile){
      data.fk_media_profile_pic_id = null
    }

    mutation.mutate(data)
  }

  const onCancel = () => {
    previousFile()
    setFile(null)

    reset()
    toggleEditMode()
  }

  return (
    <Container maxW="full">
      <Heading size="sm" py={4}>
        Kontoinformationen
      </Heading>
      <Box
        w={{ sm: "full", md: "50%" }}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Field.Root>
          <Field.Label color={color} htmlFor="firstname">
            Vorname
          </Field.Label>
          {editMode ? (
            <Input
              id="firstname"
              {...register("firstname", { maxLength: 30 })}
              type="text"
              size="md"
              w="auto"
              placeholder="Vorname"
            />
          ) : (
            <Text
              textStyle="md"
              py={2}
              color={!currentUser?.firstname ? "dim" : "inherit"}
              truncate
              maxWidth="250px"
            >
              {currentUser?.firstname || "N/A"}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label color={color} htmlFor="lastname">
            Nachname
          </Field.Label>
          {editMode ? (
            <Input
              id="lastname"
              {...register("lastname", { maxLength: 30 })}
              type="text"
              size="md"
              w="auto"
              placeholder="Nachname"
            />
          ) : (
            <Text
              textStyle="md"
              py={2}
              color={!currentUser?.lastname ? "dim" : "inherit"}
              truncate
              maxWidth="250px"
            >
              {currentUser?.lastname || "N/A"}
            </Text>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.email}>
          <Field.Label color={color} htmlFor="email">
            E-Mail
            <Field.RequiredIndicator />
          </Field.Label>
          {editMode ? (
            <Input
              id="email"
              {...register("email", {
                required: "E-Mail ist erforderlich",
                pattern: emailPattern,
              })}
              type="email"
              size="md"
              w="auto"
              placeholder="E-Mail"
            />
          ) : (
            <Text textStyle="md" py={2} truncate maxWidth="250px">
              {currentUser?.email}
            </Text>
          )}
          {errors.email && (
            <Field.ErrorText>{errors.email.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4}>
          <Field.Label>Profilbild</Field.Label>

          {editMode ? (
            <>
              {me_create ? (
                me_read ? (
                  <FileUpload.Root maxFiles={1} accept="image/*">
                    <FileUpload.HiddenInput
                      data-testid="user_image"
                      onChange={(e) => {
                        setPrevProfileFile(null)
                        setFile(e.target.files ? e.target.files[0] : null)
                        setValue("fk_media_profile_pic_id", e.target.files ? "" : null, { shouldDirty: true })
                      }}
                    />

                    <FileDropzone
                      file={prevProfileFile}
                      path={currentUser?.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")}
                      hidden={true}
                      onClick={() => {
                        setPrevProfileFile(null)
                        setFile(null)
                        setValue("fk_media_profile_pic_id", null, { shouldDirty: true })
                      }}
                    />

                    <FileUpload.Trigger asChild>
                      <Button variant="secondary" size="sm">
                        <Icon as={FiUpload} size="sm" /> Profilbild hochladen
                      </Button>
                    </FileUpload.Trigger>
                  </FileUpload.Root>
                ) : (
                  <Text color="danger">Keine Berechtigung um Bilder anzusehen</Text>
                )
              ) : (
                <Text color="danger">Keine Berechtigung um Bilder zu erstellen</Text>
              )}

              {errors.fk_media_profile_pic_id && (
                <Field.ErrorText>{errors.fk_media_profile_pic_id.message}</Field.ErrorText>
              )}
            </>
          ) : (
            me_read ? (
              <Avatar.Root
                boxShadow="rgba(0, 0, 0, 0.24) 0px 3px 8px"
                css={{
                  "--avatar-size": "sizes.32",
                }}
              >
                <Avatar.Fallback
                  name={
                    currentUser?.lastname && currentUser?.firstname
                      ? `${currentUser.lastname} ${currentUser.firstname}`
                      : currentUser?.firstname || currentUser?.lastname || currentUser?.email
                  }
                />
                <Avatar.Image src={currentUser?.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
              </Avatar.Root>
            ) : (
              <Text color="danger">Keine Berechtigung um Bilder anzusehen</Text>
            )
          )}
        </Field.Root>

        <Flex mt={4} gap={3}>
          <Button
            variant="primary"
            onClick={toggleEditMode}
            type={editMode ? "button" : "submit"}
            loading={editMode ? isSubmitting : false}
            disabled={editMode ? !isDirty || !getValues("email") : false}
          >
            {editMode ? "Speichern" : "Bearbeiten"}
          </Button>
          {editMode && (
            <Button onClick={onCancel} disabled={isSubmitting} variant="secondary">
              Abbrechen
            </Button>
          )}
        </Flex>
      </Box>
    </Container>
  )
}

export default UserInformation
